import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Pencil, Loader2, AlertTriangle } from "lucide-react";
import "./EditableCell.css";

function DisplayValue({ value, placeholder }) {
    return value
        ? <span>{value}</span>
        : <span className="text-muted-foreground italic">{placeholder}</span>;
}

export function EditableText({ value, placeholder = "—", onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { setDraft(value ?? ""); }, [value]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const commit = async () => {
        const novoValor = draft.trim();

        if (novoValor === (value ?? "")) {
            setEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(novoValor);
        } catch (e) {
            setDraft(value ?? "");
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };

    if (saving) {
        return <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />;
    }

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
                }}
                onClick={e => e.stopPropagation()}
                className="w-full min-w-[80px] px-1 py-0.5 text-[11px] border border-primary rounded-md outline-none bg-card shadow-sm"
            />
        );
    }

    return (
        <div
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            title="Clique para editar"
            className="group flex items-center gap-1 cursor-text rounded-md px-1 -mx-1 py-0 hover:bg-primary/10 transition-colors duration-150"
        >
            <DisplayValue value={value} placeholder={placeholder} />
            <Pencil className="w-3 h-3 text-primary opacity-0 group-hover:opacity-60 shrink-0 transition-opacity duration-150" />
        </div>
    );
}

export function EditableSelect({
    value,
    displayLabel,
    placeholder = "—",
    options = [],
    optionLabel = "nome",
    optionValue = "id",
    itemTemplate,
    filter = true,
    showClear = true,
    warning = false,
    warningMessage,
    onSave,
}) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const commit = async (novoValor) => {
        if (novoValor === value) {
            setEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(novoValor ?? null);
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };

    if (saving) {
        return <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />;
    }

    if (editing) {
        return (
            <div onClick={e => e.stopPropagation()} className="min-w-[130px]">
                <Dropdown
                    value={value}
                    options={options}
                    optionLabel={optionLabel}
                    optionValue={optionValue}
                    itemTemplate={itemTemplate}
                    filter={filter}
                    filterInputAutoFocus
                    showClear={showClear}
                    autoFocus
                    placeholder={placeholder}
                    onChange={e => commit(e.value)}
                    onHide={() => setEditing(false)}
                    appendTo={document.body}
                    className="escala-compact-dropdown w-full text-[11px] border border-primary rounded-md shadow-sm"
                    panelClassName="escala-compact-panel text-xs"
                />
            </div>
        );
    }

    return (
        <div
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            title={warning ? warningMessage : "Clique para editar"}
            className={`group flex items-center gap-1 cursor-pointer rounded-md px-1 -mx-1 py-0 transition-colors duration-150 ${warning ? "bg-amber-100 text-amber-900 hover:bg-amber-200" : "hover:bg-primary/10"}`}
        >
            <DisplayValue value={displayLabel} placeholder={placeholder} />
            {warning && <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />}
            <Pencil className="w-3 h-3 text-primary opacity-0 group-hover:opacity-60 shrink-0 transition-opacity duration-150" />
        </div>
    );
}
